import { Button } from "../ui/button";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import FormFieldRenderer from "../FormFieldRenderer";
import { FormSection } from "../form-builder/FormSection";
import { FormPage, FormSection as FormSectionType, FormVersionDetails } from "../../types/form-types";
import { Field } from "../../types/types";
import { evaluateFieldConditions } from "../../utils/fieldConditions";
import { getSectionsByPage, getFieldsBySection, getUnsectionedFields } from "../../utils/sectionUtils";
import { getFieldContainerClasses } from "../../utils/fieldLayoutUtils";
import { cn } from "../../lib/utils";

interface FormPageRendererProps {
  page: FormPage;
  fields: Field[];
  sections?: FormSectionType[];
  answersRecord: Record<string, any>;
  formVersion: FormVersionDetails | null;
  readOnly: boolean;
  sectionCollapsedState: Record<string, boolean>;
  currentPageIndex: number;
  visiblePages: number[];
  submitting: boolean;
  hasPreviousVisiblePage: boolean;
  hasNextVisiblePage: boolean;
  handleFieldChange: (fieldId: string, value: any) => void;
  handleSectionCollapseChange: (sectionId: string, collapsed: boolean) => void;
  openCameraModal: (field: Field, promptId?: string) => void;
  openPaymentModal: (field: Field) => void;
  handlePreviousPage: () => void;
  handlePageSubmit: () => void;
  paymentFieldStatuses?: Record<string, 'processing' | 'cancelled' | 'failed'>;
  /**
   * Amount the pre-initialize webhook bound to the transaction, major units.
   * Applied only to payment fields — it is a property of the pending payment,
   * not of the page.
   */
  preloadedAmount?: number;
  allFields?: Field[]; // All fields across all pages (needed to check payment hidden fields)
  /** Navigation mode: 'sequential' = Prev/Next buttons, 'tabs' = clickable page pills */
  navigationMode?: 'sequential' | 'tabs';
  /** All pages in the form (required for tabs mode) */
  allPages?: FormPage[];
  /** Jump to a specific page index directly (required for tabs mode) */
  onGoToPage?: (index: number) => void;
}

export function FormPageRenderer({
  page,
  fields,
  sections,
  answersRecord,
  formVersion,
  readOnly,
  sectionCollapsedState,
  currentPageIndex,
  visiblePages,
  submitting,
  hasPreviousVisiblePage,
  hasNextVisiblePage,
  handleFieldChange,
  handleSectionCollapseChange,
  openCameraModal,
  openPaymentModal,
  handlePreviousPage,
  handlePageSubmit,
  paymentFieldStatuses,
  preloadedAmount,
  allFields,
  navigationMode = 'sequential',
  allPages = [],
  onGoToPage,
}: FormPageRendererProps) {
  // Filter fields: remove hidden fields and evaluate conditional logic
  const shouldShowField = (field: Field): boolean => {
    if (field.type === 'hidden') {
      return false;
    }

    if (field.conditions && field.conditions.length > 0) {
      return evaluateFieldConditions(field, answersRecord, fields);
    }

    return true;
  };

  // Get sections and organize fields
  const allSections = sections || [];
  const pageSections = getSectionsByPage(allSections, page.id);
  const fieldsWithoutSection = getUnsectionedFields(fields, page.id).filter(shouldShowField);

  // Build errors object for validation (currently empty, but can be populated)
  const errors: Record<string, string[]> = {};

  // Calculate section validation
  const sectionValidationStatus: Record<string, { hasErrors: boolean; invalidFieldCount: number }> = {};
  pageSections.forEach((section) => {
    const sectionFields = fields.filter(
      (f) => f.sectionId === section.id && f.required
    );
    const invalidFields = sectionFields.filter(
      (f) => errors[f.id] && errors[f.id].length > 0
    );
    sectionValidationStatus[section.id] = {
      hasErrors: invalidFields.length > 0,
      invalidFieldCount: invalidFields.length,
    };
  });

  // Check if payment is required but not yet completed (disable Complete button)
  const isPaymentPending = (() => {
    if (hasNextVisiblePage) return false;
    const fieldsToCheck = allFields || fields;
    const hasPaymentField = fieldsToCheck.some((f) => f.type === 'payment');
    if (!hasPaymentField) return false;

    // Look for Transaction Status hidden field
    const statusField = fieldsToCheck.find(
      (f) => f.type === 'hidden' && (f.label === 'Transaction Status' || (f as any).dataKey === 'transactionStatus')
    );
    if (!statusField) return false;

    const statusValue = answersRecord[statusField.id];
    return statusValue !== 'completed' && statusValue !== 'success';
  })();

  // Compute page indicator outside JSX to avoid IIFE
  const visiblePosition = visiblePages.indexOf(currentPageIndex) + 1;
  const pageIndicator = visiblePages.length > 0
    ? `Page ${visiblePosition} of ${visiblePages.length}`
    : 'No visible pages';

  return (
    <div className="space-y-6">
      {/* Page Title */}
      {page.title && (
        <h3 className="text-xl font-semibold">{page.title}</h3>
      )}

      {/* Fields without sections (rendered first) */}
      {fieldsWithoutSection.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {fieldsWithoutSection.map((field) => {
            const value = answersRecord[field.id];

            return (
              <div key={field.id} className={getFieldContainerClasses(field)}>
                <FormFieldRenderer
                  field={field}
                  value={value}
                  errorClass=""
                  errors={[]}
                  formValues={answersRecord}
                  handleChange={handleFieldChange}
                  openCameraModal={openCameraModal}
                  openPaymentModal={field.type === 'payment' ? () => openPaymentModal(field) : undefined}
                  isPaymentForm={formVersion?.is_payment_form || false}
                  paymentStatus={paymentFieldStatuses?.[field.id]}
                  preloadedAmount={field.type === 'payment' ? preloadedAmount : undefined}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Render sections with their fields */}
      {pageSections.map((section) => {
        const sectionFields = getFieldsBySection(fields, section.id).filter(shouldShowField);
        const validationState = sectionValidationStatus[section.id];

        if (sectionFields.length === 0) return null;

        return (
          <FormSection
            key={section.id}
            section={section}
            collapsed={sectionCollapsedState[section.id]}
            onCollapseChange={handleSectionCollapseChange}
            hasValidationErrors={validationState?.hasErrors}
            invalidFieldCount={validationState?.invalidFieldCount}
          >
            <div className="flex flex-wrap gap-4">
              {sectionFields.map((field) => {
                const value = answersRecord[field.id];

                return (
                  <div key={field.id} className={getFieldContainerClasses(field)}>
                    <FormFieldRenderer
                      field={field}
                      value={value}
                      errorClass=""
                      errors={[]}
                      formValues={answersRecord}
                      handleChange={(fieldId: string, newValue: any) => {
                        if (!readOnly) {
                          handleFieldChange(fieldId, newValue);
                        }
                      }}
                      openCameraModal={openCameraModal}
                      openPaymentModal={field.type === 'payment' ? () => openPaymentModal(field) : undefined}
                      isPaymentForm={formVersion?.is_payment_form || false}
                      paymentStatus={paymentFieldStatuses?.[field.id]}
                      preloadedAmount={field.type === 'payment' ? preloadedAmount : undefined}
                    />
                  </div>
                );
              })}
            </div>
          </FormSection>
        );
      })}

      {/* Navigation — tabs mode: clickable page pills */}
      {navigationMode === 'tabs' && allPages.length > 1 && (
        <div className="flex flex-wrap gap-2 items-center justify-center pt-4 border-t mt-6">
          {allPages.map((p, idx) => {
            const isActive = idx === currentPageIndex;
            const isVisible = visiblePages.includes(idx);
            return (
              <button
                key={p.id}
                onClick={() => onGoToPage?.(idx)}
                disabled={!isVisible || submitting}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-150",
                  isActive
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : isVisible
                      ? "bg-muted text-muted-foreground border-border hover:bg-muted/80 hover:border-foreground/30 cursor-pointer"
                      : "bg-muted/30 text-muted-foreground/40 border-border/30 cursor-not-allowed"
                )}
              >
                {p.title || `Page ${idx + 1}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Navigation — sequential mode: Previous / Next buttons */}
      {navigationMode === 'sequential' && (
        <div className="flex justify-between items-center pt-4 border-t mt-6">
          <Button
            variant="outline"
            onClick={handlePreviousPage}
            disabled={!hasPreviousVisiblePage || submitting}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="text-sm text-muted-foreground">
            {pageIndicator}
          </div>

          <Button
            onClick={handlePageSubmit}
            disabled={submitting || isPaymentPending}
            title={isPaymentPending ? 'Complete payment before submitting' : undefined}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {hasNextVisiblePage
                  ? 'Next'
                  : 'Complete'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Navigation — tabs mode: Next / Complete button right-aligned */}
      {navigationMode === 'tabs' && (
        <div className="flex justify-end pt-4 border-t mt-6">
          <Button
            onClick={handlePageSubmit}
            disabled={submitting || isPaymentPending}
            title={isPaymentPending ? 'Complete payment before submitting' : undefined}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                {hasNextVisiblePage
                  ? 'Next'
                  : 'Complete'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
